import { z } from 'zod';

// Input sanitization utilities
export const sanitizeString = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .slice(0, 1000); // Limit length
};

export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().trim().slice(0, 255);
};

export const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^\d\+\-\(\)\s]/g, '').slice(0, 20);
};

// Enhanced validation schemas with sanitization
export const eventSchema = z.object({
  name: z.string()
    .min(1, 'Event name is required')
    .max(100, 'Event name must be less than 100 characters')
    .transform(sanitizeString),
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .transform(sanitizeString)
    .optional(),
  hostName: z.string()
    .min(1, 'Host name is required')
    .max(100, 'Host name must be less than 100 characters')
    .transform(sanitizeString),
  deadline: z.date().optional(),
  eventType: z.enum(['voting', 'rsvp']),
  rsvpDate: z.string().max(50).transform(sanitizeString).optional(),
  rsvpTime: z.string().max(20).transform(sanitizeString).optional(),
  rsvpLocation: z.string().max(200).transform(sanitizeString).optional(),
});

export const guestSchema = z.object({
  name: z.string()
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeString)
    .optional(),
  email: z.string()
    .email('Invalid email format')
    .max(255, 'Email must be less than 255 characters')
    .transform(sanitizeEmail)
    .optional(),
  phone: z.string()
    .max(20, 'Phone number must be less than 20 characters')
    .transform(sanitizePhone)
    .optional(),
}).refine(
  (data) => data.email || data.phone || data.name,
  { message: "At least one contact method is required" }
);

export const optionSchema = z.object({
  type: z.enum(['date', 'time', 'location']),
  value: z.string()
    .min(1, 'Option value is required')
    .max(200, 'Option value must be less than 200 characters')
    .transform(sanitizeString),
});

// Rate limiting utilities
const rateLimitStore = new Map<string, number[]>();

export const checkRateLimit = (key: string, maxRequests: number, windowMinutes: number): boolean => {
  const now = Date.now();
  const windowMs = windowMinutes * 60 * 1000;
  const requests = rateLimitStore.get(key) || [];
  
  // Remove old requests outside the window
  const validRequests = requests.filter(time => now - time < windowMs);
  
  if (validRequests.length >= maxRequests) {
    return false; // Rate limit exceeded
  }
  
  // Add current request
  validRequests.push(now);
  rateLimitStore.set(key, validRequests);
  
  return true; // Within rate limit
};

// Security headers for API calls
export const getSecurityHeaders = (authToken?: string) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  
  return headers;
};

// Validate UUID format
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// Validate shareable link format
export const isValidShareableLink = (link: string): boolean => {
  return /^[a-zA-Z0-9\-]{8,}$/.test(link);
};